import { Graph } from 'Common/Graph/Graph'
import { StockProjectionExceptionGraphPropsType } from './StockProjectionException.types'
import { useMemo } from 'react'
const styles = {
  lineColor: '#E7E7E7',
  fontColor: '#000',
}
const hovertemplate = ` %{customdata}<br>` + '<extra></extra>'
export const SpeGraph = ({
  graphData,
  yAxisRange,
  oneGraph = false,
}: StockProjectionExceptionGraphPropsType) => {
  const getXAxisData = (weeks: string[]) => {
    //  week is in the format of 10_24 or isoWeek_year
    // This is to resolve the issue of isoweeks being repeating if we have more that 52 weeks of data.
    const labels = weeks.map((ISOWeek: string) => {
      const week = ISOWeek.split('_')[0]
      return `${Number(week) < 10 ? 0 : ''}${week}`
    })
    return [
      Array(weeks.length)
        .fill(0)
        .map((_, i) => i.toString()),
      labels,
    ]
  }
  //xData is in the format [[indexes][labels]]
  const xData = getXAxisData(graphData.ISOWeeks)

  const xAxisTrace = {
    x: xData,
    y: Array(xData[0]?.length).fill(0),
    type: 'scatter',
    showlegend: false,
    hoverinfo: 'skip',
    marker: {
      color: '#000',
    },
    name: 'xAxis',
  }

  const fdOffsetTrace = {
    x: xData,
    y: graphData.FD_MASK,
    marker: {
      color: 'rgba(1,1,1,0.0)',
    },
    type: 'bar',
    showlegend: false,
    hoverinfo: 'skip',
  }

  const fdTrace = {
    x: xData,
    y: graphData.FD,
    type: 'bar',
    marker: {
      color: '#F59E0B',
    },
    name: 'FD',
    showlegend: false,
    hovertemplate: hovertemplate,
    customdata: graphData.toolTipTexts,
  }

  const siteOffsetTrace = {
    x: xData,
    y: graphData.SITA_MASK,
    type: 'bar',
    marker: {
      color: 'rgba(1,1,1,0.0)',
    },
    showlegend: false,
    hoverinfo: 'skip',
  }

  const sitaTace = {
    x: xData,
    y: graphData.SITA,
    type: 'bar',
    name: 'SITA',
    marker: {
      color: '#EF4444',
    },
    showlegend: false,
    hovertemplate: hovertemplate,
    customdata: graphData.toolTipTexts,
  }

  const pvaOffsetTrace = {
    x: xData,
    y: graphData.PVA_MASK,
    type: 'bar',
    marker: {
      color: 'rgba(1,1,1,0.0)',
    },
    showlegend: false,
    hoverinfo: 'skip',
  }

  const pvaTrace = {
    x: xData,
    y: graphData.PVA,
    type: 'bar',
    marker: {
      color: '#1D4ED8',
    },
    name: 'PVA',
    showlegend: false,
    hovertemplate: hovertemplate,
    customdata: graphData.toolTipTexts,
  }

  const spTrace = {
    x: xData,
    y: graphData.safetystockVolumes,
    type: 'scatter',
    showlegend: false,
    hoverinfo: 'skip',
    marker: {
      color: '#198754',
    },
    name: 'SP',
    hovertemplate: hovertemplate,
    customdata: graphData.toolTipTexts,
  }

  const updateRange = (range: [number, number]): [number, number] => {
    let [y0, y1] = range
    const graphHeight = Math.abs(y1 - y0)
    if (graphHeight > 0) {
      const exponential = Math.floor(Math.log10(y1))
      const extraHeight = 10 ** exponential / 2
      y0 = y0 - extraHeight
      y1 = y1 + extraHeight
    }
    return [y0, y1]
  }

  const data = [
    xAxisTrace,
    fdOffsetTrace,
    fdTrace,
    siteOffsetTrace,
    sitaTace,
    pvaOffsetTrace,
    pvaTrace,
    spTrace,
  ]

  const layout = useMemo(() => {
    return {
      title: {
        text: graphData.name,
        x: 0.05,
        xanchor: 'left',
        font: {
          size: 14,
          bold: true,
        },
      },
      barmode: 'stack',
      height: oneGraph ? 300 : 150,
      showlegend: false,
      xaxis: {
        ticks: 'outside',
        nticks: graphData.weeks.length / 3,
        linecolor: styles.lineColor,
        tickcolor: styles.lineColor,
        dividercolor: styles.lineColor,
        showgrid: true,
        showticklabels: true,
        tickangle: 0,
      },
      yaxis: {
        showgrid: true,
        range: updateRange(yAxisRange),
        nticks: 6,
      },
      margin: {
        b: 22,
        t: 25,
      },
    }
  }, [oneGraph])

  return <Graph data={data} layout={layout} />
}
